import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    User, EyeOff, MessageSquare, Star, ArrowLeft, 
    BarChart3, Info, Users, ShieldCheck, Target, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchSkillEvalsByUserRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchPeerConfig } from '../../../redux/actions/peerEvaluationConfigActions';

export default function MonFeedback360() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user } = useSelector(state => state.auth || {});
    const { skillEvaluations, loading } = useSelector(state => state.skillEvaluation || { skillEvaluations: [] });
    const { list: campagnes } = useSelector(state => state.campagnes || { list: [] });
    const { config: peerConfig } = useSelector(state => state.peerEvaluationConfig || {});

    const [selectedCampagneId, setSelectedCampagneId] = useState("");

    useEffect(() => {
        const currentUserId = user?.id || user?.uid || user?.personnelId;
        if (currentUserId) {
            dispatch(fetchSkillEvalsByUserRequest(currentUserId));
        }
        dispatch(fetchCampagnesRequest());
        dispatch(fetchPeerConfig());
    }, [dispatch, user]);

    const activeCampagnes = useMemo(() => (campagnes || []).filter(c => c.status === true), [campagnes]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault) || activeCampagnes[0];
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    // Récupérer l'auto-évaluation pour vérifier le statut de clôture
    const selfEval = useMemo(() => {
        return (skillEvaluations || []).find(ev => 
            ev.campagneId === parseInt(selectedCampagneId) && 
            ev.evaluateurId === null
        );
    }, [skillEvaluations, selectedCampagneId]);

    const isEvaluationClosed = useMemo(() => {
        if (!selfEval) return false;
        const s = typeof selfEval.statut === 'string' ? selfEval.statut : selfEval.statut?.name;
        return s === 'VALIDEE';
    }, [selfEval]);

    // Filtrer les feedbacks pour cette campagne qui sont des retours 360 (evaluateurId présent)
    const feedbacks = useMemo(() => {
        return (skillEvaluations || []).filter(ev => 
            ev.campagneId === parseInt(selectedCampagneId) && 
            ev.evaluateurId !== null // On ne veut que les retours des autres, pas l'auto-éval
        );
    }, [skillEvaluations, selectedCampagneId]);

    // Regrouper les feedbacks par compétence
    const skillsSummary = useMemo(() => {
        const summary = {};
        feedbacks.forEach(ev => {
            ev.niveaux?.forEach(niv => {
                if (!summary[niv.skillId]) {
                    summary[niv.skillId] = {
                        id: niv.skillId,
                        nom: niv.skillNom,
                        notes: []
                    };
                }
                summary[niv.skillId].notes.push({
                    evaluateur: ev.evaluateurFullName || "Anonyme",
                    isAnonymous: ev.evaluateurFullName?.includes("Anonyme") || peerConfig?.isAnonymous,
                    score: niv.scoreObtenu,
                    commentaire: niv.commentaire,
                    role: ev.role || "Évaluateur"
                });
            });
        });
        return Object.values(summary);
    }, [feedbacks, peerConfig]);

    const isGlobalAnonymous = peerConfig?.isAnonymous;

    return (
        <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button onClick={() => navigate(-1)} style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '8px', borderRadius: '8px', cursor: 'pointer', color: '#64748b' }}>
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Rapport de Feedback 360°</h1>
                        <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>Analyse comparative des évaluations de vos pairs et managers.</p>
                    </div>
                </div>
                <select
                    style={{ padding: '10px 16px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', minWidth: '240px', outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    value={selectedCampagneId}
                    onChange={e => setSelectedCampagneId(e.target.value)}
                >
                    <option value="">— Sélectionner une campagne —</option>
                    {activeCampagnes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                    <div className="pe-spinner" style={{ margin: '0 auto 16px' }} />
                    <p style={{ color: '#64748b' }}>Génération de votre rapport comparative...</p>
                </div>
            ) : !isEvaluationClosed ? (
                <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ background: '#f0f9ff', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#0284c7' }}>
                        <ShieldCheck size={32} />
                    </div>
                    <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>Résultats en cours de consolidation</h3>
                    <p style={{ color: '#64748b', fontSize: '15px', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
                        Vos retours 360° sont actuellement en phase de revue par votre hiérarchie. 
                        Pour garantir l'intégrité du processus, votre rapport complet sera débloqué dès que la campagne sera <strong>définitivement clôturée</strong>.
                    </p>
                    <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <div style={{ padding: '8px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={16} /> Statut actuel : <span style={{ fontWeight: 700, color: '#0ea5e9' }}>{selfEval?.statut || 'En attente'}</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={{ padding: '20px', fontSize: '13px', fontWeight: '800', color: '#475569', minWidth: '250px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 10 }}>
                                        COMPÉTENCES
                                    </th>
                                    {/* On génère les colonnes pour chaque évaluateur unique */}
                                    {feedbacks.map((fb, idx) => (
                                        <th key={fb.id} style={{ padding: '16px', textAlign: 'center', minWidth: '140px', borderLeft: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                                <div style={{ 
                                                    width: '32px', height: '32px', borderRadius: '50%', 
                                                    background: fb.evaluateurFullName?.includes("Anonyme") ? '#f1f5f9' : '#e0f2fe',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2f4595'
                                                }}>
                                                    {fb.evaluateurFullName?.includes("Anonyme") ? <EyeOff size={14} /> : <User size={14} />}
                                                </div>
                                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                                    {fb.evaluateurFullName || `Anonyme ${idx + 1}`}
                                                </span>
                                                <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>
                                                    {fb.role || 'PAIRE'}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                    <th style={{ padding: '16px', textAlign: 'center', minWidth: '100px', borderLeft: '2px solid #e2e8f0', background: '#f0f9ff', color: '#0369a1', fontWeight: '800', fontSize: '12px' }}>
                                        MOYENNE 360
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {skillsSummary.map((skill, sIdx) => {
                                    const avg = skill.notes.length > 0 
                                        ? (skill.notes.reduce((sum, n) => sum + n.score, 0) / skill.notes.length).toFixed(1)
                                        : '—';

                                    return (
                                        <tr key={skill.id} style={{ borderBottom: '1px solid #f1f5f9', background: sIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                            <td style={{ padding: '16px 20px', position: 'sticky', left: 0, background: sIdx % 2 === 0 ? '#fff' : '#fafafa', zIndex: 5 }}>
                                                <div style={{ fontWeight: '700', color: '#334155', fontSize: '14px' }}>{skill.nom}</div>
                                            </td>
                                            
                                            {feedbacks.map(fb => {
                                                const noteObj = skill.notes.find(n => n.evaluateur === (fb.evaluateurFullName || "Anonyme"));
                                                return (
                                                    <td key={fb.id} style={{ padding: '12px', textAlign: 'center', borderLeft: '1px solid #f1f5f9' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                                            {noteObj ? (
                                                                <>
                                                                    <div style={{ 
                                                                        width: '34px', height: '34px', borderRadius: '8px', 
                                                                        background: noteObj.score >= 3 ? '#ecfdf5' : noteObj.score >= 2 ? '#f9fefd' : '#fef2f2',
                                                                        color: noteObj.score >= 3 ? '#059669' : noteObj.score >= 2 ? '#707ea7' : '#dc2626',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '15px', border: '1px solid currentColor'
                                                                    }}>
                                                                        {noteObj.score}
                                                                    </div>
                                                                    {noteObj.commentaire && (
                                                                        <div title={noteObj.commentaire} style={{ cursor: 'help' }}>
                                                                            <MessageSquare size={12} color="#94a3b8" />
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span style={{ color: '#cbd5e1' }}>—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            
                                            <td style={{ padding: '12px', textAlign: 'center', borderLeft: '2px solid #e2e8f0', background: '#f0f9ff' }}>
                                                <div style={{ fontSize: '16px', fontWeight: '900', color: '#0369a1' }}>
                                                    {avg}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Légende */}
            <div style={{ marginTop: '24px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#ecfdf5', border: '1px solid #059669' }} /> Maîtrise élevée
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#f9fefd', border: '1px solid #707ea7' }} /> En acquisition
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#fef2f2', border: '1px solid #dc2626' }} /> À développer
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b', marginLeft: 'auto' }}>
                    <MessageSquare size={12} /> Survoler pour voir les commentaires
                </div>
            </div>

            {/* Info footer */}
            <div style={{ marginTop: '40px', padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Info size={18} color="#2f4595" style={{ marginTop: '2px' }} />
                <div>
                    <h4 style={{ margin: 0, fontSize: '14px', color: '#1e293b' }}>À propos de vos résultats 360°</h4>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
                        Ces notes sont utilisées pour calculer votre moyenne consolidée dans le rapport final de campagne. 
                        Les commentaires sont là pour vous aider à identifier vos points forts et vos axes de développement perçus par votre entourage professionnel.
                    </p>
                </div>
            </div>
        </div>
    );
}
